import { CalendarOutlined, PlusOutlined } from '@ant-design/icons'
import { GraviButton, GraviGrid, Horizontal, Texto, Vertical, useLocalStorage } from '@gravitate-js/excalibrr'
import type { AgGridReactProps } from 'ag-grid-react'
import { Switch, notification } from 'antd'
import { useCallback, useMemo, useRef, useState } from 'react'

import { formatDriverForUpdate, useDriverManagement } from '@/api/useDriverManagement'
import type { components } from '@/types/api/v1'
import api from '@/utils/api'
import type { FirstDataRenderedEvent, GridApi } from 'ag-grid-community'
import { useSecurityContext } from '../../Navigation/SecurityContext'
import { UploadDownloadButtons } from '../../_SiteWide/UploadStatus/UploadDownloadButtons'
import AddDriverDrawer from './AddDriverDrawer'
import { PayConfigModal } from './Form/PayConfigModal'
import type { ScheduleTemplateValue } from './GridColumns/ScheduleTemplateEditor'
import { PasswordChangeModal } from './PasswordChangeModal'
import { TemplateDrawer } from './TemplateManagement/TemplateDrawer'
import { driverInfoColumns } from './driverGridColumnDefs'
import { useSearchParams } from 'react-router-dom'

type Row = components['schemas']['DriverInfoRow']
function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined
}

export function DriverGrid() {
  const [notificationApi, contextHolder] = notification.useNotification()

  //todo: change to use column filtering on grid
  const { value: onlyActiveDrivers, setValue: setOnlyActiveDrivers } = useLocalStorage(
    'only_active_drivers_driver_management',
    true
  )
  const { useDriverOverview, useDriverUpdate, useDriverMetadata } = useDriverManagement()
  const {
    data: drivers = [],
    isLoading: isDriversLoading,
    refetch: refetchDrivers,
  } = useDriverOverview({
    onlyActiveDrivers: !!onlyActiveDrivers,
  })

  const { data: metadataData, isLoading: isMetadataLoading } = useDriverMetadata()

  // Get mutation for saving driver changes
  const upsertDriverMutation = useDriverUpdate()
  const { scopes } = useSecurityContext()
  const gridAPIRef = useRef<GridApi>(undefined)
  const [selectedDriver, setSelectedDriver] = useState<Row | null>(null)
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState<boolean>(false)
  const [isAddDriverVisible, setIsAddDriverVisible] = useState(false)
  const [isTemplateDrawerVisible, setIsTemplateDrawerVisible] = useState(false)
  const [isPayConfigModalVisible, setIsPayConfigModalVisible] = useState(false)

  const [searchParams] = useSearchParams()
  const driverIdQueryParam = useMemo(() => searchParams.get('id'), [searchParams])

  const canEditDriverInfo = !!scopes?.driver?.info.edit
  const canChangePassword = !!scopes?.driver?.info.password

  const handlePasswordChange = useCallback((driver?: Row) => {
    if (!driver) return
    setSelectedDriver(driver)
    setIsPasswordModalVisible(true)
  }, [])

  const handlePayConfigClick = useCallback((driver?: Row) => {
    if (!driver) return
    setSelectedDriver(driver)
    setIsPayConfigModalVisible(true)
  }, [])

  const updateDriver = (driver: Row) =>
    upsertDriverMutation
      .mutateAsync(formatDriverForUpdate(driver))
      .then((result) => {
        notificationApi.success({
          message: 'Driver Updated',
          description: `Successfully updated ${driver.name}'s information`,
          placement: 'bottomRight',
          duration: 2,
        })
        return result
      })
      .catch((error) => {
        console.error('Error updating driver:', error)
        notificationApi.error({
          message: 'Update Failed',
          description: 'There was an error updating the driver information',
          placement: 'bottomRight',
        })
        throw error
      })

  const handleTemplateSave = async (driver: Row, value: ScheduleTemplateValue) =>
    updateDriver({ ...driver, ...value }).then(() => {
      if (value.preferred_template_id) {
        return api.autoGenerateSchedules(driver.id)
      }
    })

  // Handle opening the Add Driver drawer
  const openAddDriver = () => {
    setIsAddDriverVisible(true)
  }

  const columnDefs = useMemo(
    () =>
      driverInfoColumns(
        canEditDriverInfo,
        canChangePassword,
        !!onlyActiveDrivers,
        handlePasswordChange,
        handlePayConfigClick,
        handleTemplateSave
      ).filter(notEmpty),
    [canEditDriverInfo, canChangePassword, onlyActiveDrivers, handlePasswordChange, handlePayConfigClick]
  )

  const agPropOverrides = useMemo<AgGridReactProps<Row>>(
    () => ({
      getRowId: (params) => params.data.id,
      enableRangeSelection: true,
      rowSelection: 'multiple',
      tooltipShowDelay: 0,
      rowHeight: 50,
      context: {
        metadata: metadataData,
        templates: metadataData?.templates ?? [],
        usingRefreshedData: true,
      },
      statusBar: {
        statusPanels: [
          { statusPanel: 'agTotalAndFilteredRowCountComponent' },
          { statusPanel: 'agSelectedRowCountComponent' },
        ],
      },
      maintainColumnOrder: false,
    }),
    [metadataData]
  )

  return (
    <>
      {contextHolder}
      <PasswordChangeModal
        isVisible={isPasswordModalVisible}
        setIsVisible={setIsPasswordModalVisible}
        driver={selectedDriver}
      />
      <PayConfigModal
        driver={selectedDriver}
        showingModal={isPayConfigModalVisible}
        setShowingModal={() => setIsPayConfigModalVisible(false)}
      />
      <TemplateDrawer isVisible={isTemplateDrawerVisible} onClose={() => setIsTemplateDrawerVisible(false)} />
      <AddDriverDrawer isVisible={isAddDriverVisible} onClose={() => setIsAddDriverVisible(false)} />

      {/** main grid */}
      <Vertical>
        <GraviGrid<Row, Record<string, unknown>>
          externalRef={gridAPIRef}
          storageKey='driver-management-grid-combined'
          controlBarProps={{
            title: 'Driver Management',
            actionButtons: (
              <Horizontal gap={16} alignItems='center'>
                <UploadDownloadButtons
                  fileName='Drivers.xlsx'
                  refreshCallback={refetchDrivers}
                  downloadEP={api.downloadDrivers}
                  uploadEP={canEditDriverInfo && api.uploadDrivers}
                />
                <Horizontal gap={8} verticalCenter>
                  <Texto category='label'>Only Active</Texto>
                  <Switch
                    checked={!!onlyActiveDrivers}
                    onChange={(checked) => {
                      setOnlyActiveDrivers(checked)
                    }}
                  />

                  {canEditDriverInfo && (
                    <>
                      <GraviButton
                        buttonText='Manage Templates'
                        icon={<CalendarOutlined />}
                        theme2
                        appearance='outlined'
                        onClick={() => setIsTemplateDrawerVisible(true)}
                      />
                      <GraviButton
                        buttonText='Add Driver'
                        icon={<PlusOutlined />}
                        theme2
                        onClick={openAddDriver}
                        data-cy='add-driver-btn'
                      />
                    </>
                  )}
                </Horizontal>
              </Horizontal>
            ),
          }}
          agPropOverrides={agPropOverrides}
          rowData={drivers}
          columnDefs={columnDefs}
          loading={isDriversLoading || isMetadataLoading}
          updateEP={
            canEditDriverInfo ? (d) => Promise.allSettled((Array.isArray(d) ? d : [d]).map(updateDriver)) : undefined
          }
          onFirstDataRendered={(params: FirstDataRenderedEvent) => {
            if (!driverIdQueryParam) return
            const driverName = drivers.find((d) => d.id === driverIdQueryParam)?.name ?? ''
            params.api.setFilterModel({
              name: {
                filterType: 'text',
                type: 'equals',
                filter: driverName,
              },
            })
          }}
        />
      </Vertical>
    </>
  )
}
